import { type ReactNode, useState } from 'react';

import {
  InputAdornment,
  type SxProps,
  TextField,
  type TextFieldProps,
  type Theme,
  useTheme,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';

import { mergeSx } from '@assets/helpers/utils';

import { ClosedEyeIcon } from '../../assets/icons/closed-eye.tsx';
import { EyeIcon } from '../../assets/icons/Eye.tsx';
import { Button, type ButtonProps } from '../button';
import { inputStyles } from './styles';

export type InputButtonProps = Omit<ButtonProps, 'children'> & {
  label: string;
};

type InputSlotProps = {
  autoComplete?: string;
  endAdornment?: ReactNode;
  sx?: SxProps<Theme>;
};

type HtmlInputSlotProps = {
  autoComplete?: string;
  sx?: SxProps<Theme>;
};

export type InputPT = Omit<TextFieldProps, 'variant'> & {
  isPasswordShown?: boolean;
  onShowPasswordClick?: () => void;
  buttonProps?: InputButtonProps;
  filledInputSx?: SxProps<Theme>;
  htmlInputSx?: SxProps<Theme>;
};

export const Input = (props: InputPT) => {
  const {
    type,
    slotProps: slotsFromParent,
    isPasswordShown,
    onShowPasswordClick,
    buttonProps,
    disabled,
    filledInputSx,
    htmlInputSx,
    sx,
    autoComplete = 'off',
    ...rest
  } = props;

  const theme = useTheme();
  const [show, setShow] = useState(isPasswordShown ?? false);

  const isPassword = type === 'password';

  const parentInputSlot = (
    typeof slotsFromParent?.input === 'function' ? {} : (slotsFromParent?.input ?? {})
  ) as InputSlotProps;
  const parentHtmlInputSlot = (
    typeof slotsFromParent?.htmlInput === 'function' ? {} : (slotsFromParent?.htmlInput ?? {})
  ) as HtmlInputSlotProps;

  const {
    label: buttonLabel,
    variantStyle = 'secondary',
    size = 'small',
    disabled: isButtonDisabledManually,
    ...restButtonProps
  } = buttonProps ?? {};

  const buttonEndAdornment = buttonProps ? (
    <InputAdornment position="end" sx={{ alignSelf: 'end' }}>
      <Button
        variantStyle={variantStyle}
        size={size}
        disabled={disabled || isButtonDisabledManually}
        {...restButtonProps}
      >
        {buttonLabel}
      </Button>
    </InputAdornment>
  ) : null;

  const passwordEndAdornment = isPassword ? (
    <InputAdornment position="end">
      <IconButton
        disabled={disabled}
        aria-label="toggle password visibility"
        onClick={() => {
          if (onShowPasswordClick) {
            onShowPasswordClick();
          } else {
            setShow((prev) => !prev);
          }
        }}
        onMouseDown={(e) => e.preventDefault()}
        edge="end"
      >
        {show ? <ClosedEyeIcon /> : <EyeIcon />}
      </IconButton>
    </InputAdornment>
  ) : null;

  const hasEndAdornment = Boolean(
    parentInputSlot.endAdornment || buttonEndAdornment || passwordEndAdornment
  );
  const endAdornment = hasEndAdornment ? (
    <>
      {parentInputSlot.endAdornment}
      {buttonEndAdornment}
      {passwordEndAdornment}
    </>
  ) : undefined;

  return (
    <TextField
      {...rest}
      disabled={disabled}
      type={isPassword ? (show ? 'text' : 'password') : type}
      autoComplete={autoComplete}
      fullWidth
      variant="filled"
      slotProps={{
        ...slotsFromParent,

        input: {
          ...parentInputSlot,
          autoComplete: parentInputSlot.autoComplete ?? autoComplete,
          sx: mergeSx(parentInputSlot.sx, filledInputSx),
          endAdornment,
        },

        htmlInput: {
          ...parentHtmlInputSlot,
          autoComplete: parentHtmlInputSlot.autoComplete ?? autoComplete,
          sx: mergeSx(parentHtmlInputSlot.sx, htmlInputSx),
        },
      }}
      sx={[
        inputStyles(theme, Boolean(buttonEndAdornment)),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};
